import { useState, useCallback } from 'react';
import { supabase } from '../services/supabaseService';

/**
 * Hook do zarządzania edycją ogłoszeń
 * Zgodnie z best practices - wydzielona logika biznesowa
 */
export const useEditListing = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Rozpoczyna edycję ogłoszenia - pobiera pełne dane z bazy
   */
  const startEditing = useCallback(async (propertyId) => {
    try {
      setIsLoading(true);
      setError(null);
      
      
      // Pobierz pełne dane ogłoszenia z bazy danych
      const { data: property, error: fetchError } = await supabase
        .from('properties')
        .select('*')
        .eq('id', propertyId)
        .single();
      
      if (fetchError) {
        throw new Error(`Błąd pobierania danych: ${fetchError.message}`);
      }
      
      if (!property) {
        throw new Error('Nie znaleziono ogłoszenia');
      }
      
      
      setEditingProperty(property);
      setIsEditing(true);
      
      
      return property;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Kończy edycję i czyści stan
   */
  const stopEditing = useCallback(() => {
    setIsEditing(false);
    setEditingProperty(null);
    setError(null);
    setIsLoading(false); // Also reset loading state
  }, []);

  /**
   * Aktualizuje ogłoszenie w bazie danych
   */
  const updateListing = useCallback(async (propertyId, updatedData) => {
    try {
      setIsLoading(true);
      setError(null);
      
      
      // Usuń pola które nie powinny być aktualizowane
      const {
        id,
        created_at,
        updated_at,
        ...dataToUpdate
      } = updatedData;
      
      // Dodaj timestamp aktualizacji
      dataToUpdate.updated_at = new Date().toISOString();
      
      const { data: updatedProperty, error: updateError } = await supabase
        .from('properties')
        .update(dataToUpdate)
        .eq('id', propertyId)
        .select()
        .single();
      
      if (updateError) {
        throw new Error(`Błąd aktualizacji: ${updateError.message}`);
      }
      
      
      return updatedProperty;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    // Stan
    isEditing,
    editingProperty,
    isLoading,
    error,
    
    // Akcje
    startEditing,
    stopEditing,
    updateListing
  };
};